import { useEffect, useState, type ReactNode } from "react";

import { css, keyframes } from "@compiled/react";
import {
    getRichTextTagsFromTextFormat,
    isRichTextNodeType,
    isRichTextValueConsideredEmpty
} from "@uniformdev/richtext";

import { wacToken } from "@/libraries/design/foundations";

import type {
    ParameterRichTextValue,
    RichTextNode
} from "@uniformdev/richtext";

const terminalGlowInKeyframes = keyframes({
    "0%": { opacity: 0, filter: "blur(8px)" },
    "100%": { opacity: 1, filter: "blur(0)" }
});

const terminalGlowInCss = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    animation: `2s ease-out 1s both ${terminalGlowInKeyframes}`
});

const outerCss = css({
    display: "inline-block",
    boxSizing: "border-box",
    transform: "scale(0.9)",
    transformOrigin: "top left",
    position: "relative"
});

const textStaticCss = css({
    lineHeight: 1,
    fontWeight: 500,
    // eslint-disable-next-line local/ensure-wac-token-usage
    letterSpacing: "-0.1em",
    whiteSpace: "nowrap"
});

const rowCss = css({
    display: "inline-flex",
    alignItems: "center"
});

const gridContainerCss = css({
    display: "grid",
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr"
});

const cursorBaseCss = css({
    display: "inline-block",
    // eslint-disable-next-line local/ensure-wac-token-usage
    marginInlineStart: "0.04em",
    borderRadius: 4,
    transition: "none",
    flex: "0 0 auto"
});

const cursorWrapperCss = css({
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    flex: "0 0 auto"
});

const bgLayerCss = css({
    position: "absolute",
    inset: 0,
    zIndex: -1,
    pointerEvents: "none",
    backgroundColor: wacToken("color.primary.black")
});

const segmentPuffCss = css({
    position: "absolute",
    insetInlineStart: "-60%",
    insetInlineEnd: "-60%",
    // eslint-disable-next-line local/ensure-wac-token-usage
    filter: "blur(10px)",
    pointerEvents: "none",
    zIndex: -2
});

const coloredSegmentWrapperCss = css({
    position: "relative",
    display: "inline-block"
});

// Both grid cells (ghost + visible) stack in the same single grid track.
const gridCellCss = css({
    gridColumn: 1,
    gridRow: 1
});

// The ghost cell only reserves space — it is never shown.
const ghostCellCss = css({
    visibility: "hidden"
});

const cursorPuffCss = css({
    position: "absolute",
    insetInlineStart: "-150%",
    insetInlineEnd: "-150%",
    // eslint-disable-next-line local/ensure-wac-token-usage
    filter: "blur(10px)",
    pointerEvents: "none",
    zIndex: -2,
    transition: "none"
});

export interface TerminalTypingSettings {
    typeMs: number;
    backspaceMs: number;
    holdMs: number;
    pauseMs: number;
    blinkMs: number;
    fontSizePt: number;
    /** Explicit box width in px, or null to use the ghost-measured natural width. */
    boxWidthPx: number | null;
    paddingPx: number;
    borderRadiusPx: number;
}

export const TERMINAL_TYPING_DEFAULTS: TerminalTypingSettings = {
    typeMs: 60,
    backspaceMs: 35,
    holdMs: 1500,
    pauseMs: 400,
    blinkMs: 500,
    fontSizePt: 36,
    boxWidthPx: null,
    paddingPx: 16,
    borderRadiusPx: 12
};

interface Segment {
    text: string;
    color: string;
}

const PROMPT_COLOR = "#B1E4F7";
const WHITE = wacToken("color.neutral.light0");

/**
 * The set of highlight colors an editor may pick for a phrase's emphasized
 * (italic) word.
 */
export const HIGHLIGHT_COLORS = {
    lime: wacToken("color.extended.lime400"),
    // These three colors have no design-token equivalent;
    /* eslint-disable local/ensure-wac-token-usage */
    cyan: "#7BD3EA",
    saffron: wacToken("color.extended.saffron300"),
    coral: "#F87B6B",
    blue: "#1E88FF"
    /* eslint-enable local/ensure-wac-token-usage */
} as const;

export type HighlightColor =
    (typeof HIGHLIGHT_COLORS)[keyof typeof HIGHLIGHT_COLORS];

const DEFAULT_HIGHLIGHT_COLOR: HighlightColor = HIGHLIGHT_COLORS.lime;

/**
 * A single phrase as supplied by Uniform: the translated rich-text value
 * (where the editor italicizes the word to highlight) plus the highlight
 * color to paint that italic word.
 */
export interface PhraseInput {
    /** Uniform rich-text value. The italic (`<em>`) run is the highlighted word. */
    richText?: ParameterRichTextValue | { root: RichTextNode };
    /** One of {@link HIGHLIGHT_COLORS}. Falls back to {@link DEFAULT_HIGHLIGHT_COLOR}. */
    highlightColor?: string;
}

/** Extract the `{ root }` node from either Uniform value shape. */
function getRichTextRoot(
    value: PhraseInput["richText"]
): RichTextNode | undefined {
    if (!value || typeof value !== "object") return undefined;
    const root = (value as { root?: unknown }).root;
    return root && typeof root === "object"
        ? (root as RichTextNode)
        : undefined;
}

/**
 * Walk a Uniform rich-text tree and collapse it into the terminal's
 * `[white, highlighted, white, ...]` segment shape. Any text node whose
 * format includes italic (`<em>`) becomes a highlighted segment painted in
 * `highlightColor`; everything else is white. Consecutive runs of the same
 * color are merged so the typing animation advances one visual word at a time.
 * The editor may place the italic word anywhere in the phrase.
 */
function richTextToSegments(
    root: RichTextNode,
    highlightColor: string
): Segment[] {
    const segments: Segment[] = [];
    const pushText = (text: string, isItalic: boolean) => {
        if (!text) return;
        const color = isItalic ? highlightColor : WHITE;
        const last = segments[segments.length - 1];
        if (last && last.color === color) {
            last.text += text;
        } else {
            segments.push({ text, color });
        }
    };
    const walk = (node: RichTextNode) => {
        if (isRichTextNodeType(node, "text")) {
            const isItalic = getRichTextTagsFromTextFormat(
                node.format
            ).includes("em");
            pushText(node.text, isItalic);
            return;
        }
        node.children?.forEach(walk);
    };
    walk(root);
    return segments;
}

/**
 * Convert the editor-supplied phrases into the `Segment[][]` the typing
 * animation consumes. Empty phrases (no rich text, or rich text considered
 * empty by Uniform) are skipped so unused slots behave as if absent. Each
 * phrase is prefixed with the shared `>` prompt segment.
 */
function buildPhrases(phraseInputs: PhraseInput[]): Segment[][] {
    const promptSegment: Segment = { text: ">", color: PROMPT_COLOR };
    const phrases: Segment[][] = [];
    for (const input of phraseInputs) {
        const root = getRichTextRoot(input.richText);
        if (
            !root ||
            isRichTextValueConsideredEmpty(
                input.richText as ParameterRichTextValue
            )
        ) {
            continue;
        }
        const highlightColor = input.highlightColor || DEFAULT_HIGHLIGHT_COLOR;
        const segments = richTextToSegments(root, highlightColor);
        if (segments.length === 0) continue;
        phrases.push([promptSegment, ...segments]);
    }
    return phrases;
}

type Breakpoint = "mobile" | "tablet" | "desktop";

interface SizeSpec {
    fontSize: string;
    cursorWidth: number;
    cursorHeight: number;
    padding: number;
    promptGap: number;
    borderRadius: number;
}

// Tablet/mobile sizing keeps the hero terminal readable on small screens.
// On desktop we fall back to the live `TerminalTypingSettings` (which the
// floating Typing controls panel can tune) — see the merge of `size` and
// `settings` below.
const SIZE_BY_BREAKPOINT: Record<Breakpoint, SizeSpec> = {
    desktop: {
        fontSize: "36pt",
        cursorWidth: 39,
        cursorHeight: 74,
        padding: 14,
        promptGap: 16,
        borderRadius: 12
    },
    tablet: {
        fontSize: "24pt",
        cursorWidth: 21,
        cursorHeight: 39,
        padding: 12,
        promptGap: 12,
        borderRadius: 10
    },
    mobile: {
        fontSize: "18pt",
        cursorWidth: 15,
        cursorHeight: 30,
        padding: 10,
        promptGap: 10,
        borderRadius: 8
    }
};

function getBreakpoint(width: number): Breakpoint {
    if (width >= 1024) return "desktop";
    if (width >= 768) return "tablet";
    return "mobile";
}

function useBreakpoint(): Breakpoint {
    const [bp, setBp] = useState<Breakpoint>(() => {
        if (typeof window === "undefined") return "desktop";
        return getBreakpoint(window.innerWidth);
    });
    useEffect(() => {
        if (typeof window === "undefined") return;
        const onResize = () => setBp(getBreakpoint(window.innerWidth));
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    return bp;
}

function phraseLength(segments: Segment[]) {
    return segments.reduce((n, s) => n + s.text.length, 0);
}

function findLongestIndex(phrases: Segment[][]): number {
    let idx = 0;
    let max = -1;
    phrases.forEach((p, i) => {
        const len = phraseLength(p);
        if (len > max) {
            max = len;
            idx = i;
        }
    });
    return idx;
}

// Number of characters in the prompt segment (`>`). Backspacing stops here
// so the prompt stays visible across phrase swaps and the cursor parks
// at the position of the first letter (after the prompt gap).
const PROMPT_LENGTH = 1;

function renderSegments(
    segments: Segment[],
    charLimit: number,
    promptGap: number,
    paddingPx: number,
    visible: boolean
): ReactNode[] {
    const nodes: ReactNode[] = [];
    let remaining = charLimit;
    for (let i = 0; i < segments.length; i++) {
        const seg = segments[i] as Segment;
        // Add a viewport-scaled gap immediately after the prompt segment ('>')
        const isPrompt = i === 0;
        const style: React.CSSProperties = { color: seg.color };
        if (isPrompt) style.marginRight = promptGap;
        if (remaining <= 0) {
            nodes.push(
                <span key={i} style={style}>
                    {""}
                </span>
            );
            continue;
        }
        const slice = seg.text.slice(0, remaining);
        const isColoured = seg.color !== WHITE;
        // For coloured segments we wrap the letters in a relative inline-block
        // and drop two blurred "puffs" of the segment's colour that sit just
        // outside the terminal — above and below — so they tint only the
        // slice of the surrounding blue halo that lines up with the coloured
        // word. The rest of the halo stays its original blue.
        // The hidden ghost render (used to lock the terminal's width) must
        // NOT paint puffs, otherwise they would show up at the longest
        // phrase's positions even before that phrase types in.
        if (isColoured && visible) {
            const c = seg.color;
            // Tall puff that extends from outside the terminal edge into the
            // terminal interior. The colored span's wrapper has no stacking
            // context, so the puff's `zIndex: -2` bubbles up to the outer
            // terminal stacking context where the opaque background layer
            // (`zIndex: -1`) covers the inside portion — leaving only the
            // sliver that pokes past the terminal edge visible against the
            // surrounding blue halo.
            const sliver = 6;
            const puffHeight = Math.max(36, paddingPx * 2.6);
            const edgeOffset = -(paddingPx + sliver);
            // Runtime-only puff values (the rest live in segmentPuffCss).
            const sharedPuff: React.CSSProperties = {
                height: puffHeight,
                background: `radial-gradient(50% 100% at 50% 50%, ${c}E6 0%, ${c}80 35%, ${c}33 65%, transparent 85%)`
            };
            nodes.push(
                <span key={i} css={coloredSegmentWrapperCss} style={style}>
                    <span
                        aria-hidden
                        css={segmentPuffCss}
                        style={{ ...sharedPuff, top: edgeOffset }}
                    />
                    <span
                        aria-hidden
                        css={segmentPuffCss}
                        style={{ ...sharedPuff, bottom: edgeOffset }}
                    />
                    {slice}
                </span>
            );
        } else {
            nodes.push(
                <span key={i} style={style}>
                    {slice}
                </span>
            );
        }
        remaining -= slice.length;
    }
    return nodes;
}

function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState<boolean>(() => {
        if (typeof window === "undefined" || !window.matchMedia) return false;
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    });
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const handler = () => setReduced(mq.matches);
        mq.addEventListener?.("change", handler);
        return () => mq.removeEventListener?.("change", handler);
    }, []);
    return reduced;
}

type Phase = "typing" | "backspacing";

const CURSOR_PUFF_GRADIENT = `radial-gradient(50% 100% at 50% 50%, #FFFFFFCC 0%, #FFFFFF66 35%, #FFFFFF22 65%, transparent 85%)`;

interface TerminalCursorProps {
    cursorStyle: React.CSSProperties;
    padding: number;
    showCursor: boolean;
}

// The blinking cursor plus its top/bottom white halo "puffs" that poke past
// the terminal edge. Extracted to keep the main component's complexity down.
const TerminalCursor = ({
    cursorStyle,
    padding,
    showCursor
}: TerminalCursorProps) => {
    const sliver = 6;
    const puffHeight = Math.max(36, padding * 2.6);
    const edgeOffset = -(padding + sliver);
    const puffStyle: React.CSSProperties = {
        height: puffHeight,
        background: CURSOR_PUFF_GRADIENT,
        opacity: showCursor ? 0.5 : 0
    };
    return (
        <span css={cursorWrapperCss}>
            <span
                aria-hidden
                css={cursorPuffCss}
                style={{ ...puffStyle, top: edgeOffset }}
            />
            <span
                aria-hidden
                css={cursorPuffCss}
                style={{ ...puffStyle, bottom: edgeOffset }}
            />
            <span css={cursorBaseCss} style={cursorStyle} />
        </span>
    );
};

interface ResolvedSizing {
    cursorWidth: number;
    cursorHeight: number;
    fontSize: string;
    padding: number;
    borderRadius: number;
    promptGap: number;
}

/**
 * Resolve the terminal's pixel sizing for the current breakpoint. On desktop the
 * appearance is driven by `settings` (so the Typing controls panel can tune it
 * live); on tablet/mobile it falls back to the breakpoint-tuned `size` so the
 * hero terminal stays readable on small screens. Cursor dimensions scale with
 * font size on desktop (height = 1.6×, width = 0.84×) to keep proportion across
 * the full size range. Extracted from the component to keep its complexity down.
 */
function resolveSizing(
    settings: TerminalTypingSettings,
    breakpoint: Breakpoint
): ResolvedSizing {
    const size = SIZE_BY_BREAKPOINT[breakpoint];
    if (breakpoint !== "desktop") {
        return {
            cursorWidth: size.cursorWidth,
            cursorHeight: size.cursorHeight,
            fontSize: size.fontSize,
            padding: size.padding,
            borderRadius: size.borderRadius,
            promptGap: size.promptGap
        };
    }
    return {
        cursorWidth: settings.fontSizePt * 0.84,
        cursorHeight: settings.fontSizePt * 1.6,
        fontSize: `${settings.fontSizePt}pt`,
        padding: settings.paddingPx,
        borderRadius: settings.borderRadiusPx,
        // Prompt gap is responsive across all breakpoints (not tunable).
        promptGap: size.promptGap
    };
}

/** Number of fixed phrase slots exposed to Uniform editors. */
export const MAX_PHRASES = 8;

export interface TerminalTypingProps {
    settings?: TerminalTypingSettings;
    /**
     * When true, the typing animation finishes the current phrase (letting any
     * in-progress type/backspace complete) and then holds — it will not advance
     * to the next phrase until `paused` becomes false again.
     */
    paused?: boolean;
    /** Static heading rendered above the terminal. Translated by Uniform. */
    topLine?: string;
    /**
     * Up to {@link MAX_PHRASES} phrases. Each is a Uniform rich-text value where
     * the italic run is the highlighted word. Empty phrases are skipped, so
     * unused slots behave as if they were never supplied.
     */
    phrases?: PhraseInput[];
}

export const UniformTerminalTyping = ({
    settings = TERMINAL_TYPING_DEFAULTS,
    paused = false,
    topLine,
    phrases: phraseInputs
}: TerminalTypingProps) => {
    const reducedMotion = usePrefersReducedMotion();
    const breakpoint = useBreakpoint();
    const isDesktop = breakpoint === "desktop";
    const phrases = buildPhrases(phraseInputs ?? []);
    const longestIndex = findLongestIndex(phrases);
    const [phraseIndex, setPhraseIndex] = useState(0);
    // Start at PROMPT_LENGTH so the leading `>` is visible from first paint
    // and never gets typed/erased during the loop.
    const [charCount, setCharCount] = useState(PROMPT_LENGTH);
    const [phase, setPhase] = useState<Phase>("typing");
    const [cursorVisible, setCursorVisible] = useState(true);

    // `phrases` can be empty if every slot was left blank. Default to an empty
    // segment list so the typing effects below never read `undefined`; the
    // component early-returns `null` (after all hooks) when there are none.
    const currentSegments = (phrases[phraseIndex] ?? []) as Segment[];
    const currentTotal = phraseLength(currentSegments);
    // Per spec: cursor blinks only after a phrase is fully typed, until
    // backspacing begins. While typing, backspacing, or in the pause
    // between phrases, the cursor stays solid.
    const idle = phase === "typing" && charCount >= currentTotal;

    // Drive typing / backspacing
    useEffect(() => {
        if (reducedMotion) return;
        let id: ReturnType<typeof setTimeout> | undefined;
        if (phase === "typing") {
            if (charCount < currentTotal) {
                // Always let an in-progress phrase finish typing — even while paused —
                // so the visible word lands on a complete state.
                id = setTimeout(
                    () => setCharCount((c) => c + 1),
                    settings.typeMs
                );
            } else if (!paused) {
                // Fully typed: hold, then start backspacing. While paused, hold here
                // indefinitely (no schedule) until `paused` flips back to false.
                id = setTimeout(() => setPhase("backspacing"), settings.holdMs);
            }
        } else {
            if (charCount > PROMPT_LENGTH) {
                // Backspacing is also part of "the current message finishing", so we
                // let it complete even while paused.
                id = setTimeout(
                    () => setCharCount((c) => c - 1),
                    settings.backspaceMs
                );
            } else {
                // Erased back to the prompt: always advance to the next phrase, even
                // while paused. This way, if pause arrived mid-backspace, we still
                // settle on a fully-typed phrase (the next one) rather than holding
                // on the bare prompt. The hold itself happens in the `phase==='typing'
                // && fully typed` branch above.
                id = setTimeout(() => {
                    setPhraseIndex((i) => (i + 1) % phrases.length);
                    setPhase("typing");
                }, settings.pauseMs);
            }
        }
        return () => {
            if (id) clearTimeout(id);
        };
    }, [
        phase,
        charCount,
        currentTotal,
        reducedMotion,
        paused,
        settings.typeMs,
        settings.backspaceMs,
        settings.holdMs,
        settings.pauseMs
    ]);

    // Cursor blink — only while idle (fully typed or fully erased)
    useEffect(() => {
        if (reducedMotion) {
            setCursorVisible(true);
            return;
        }
        if (!idle) {
            setCursorVisible(true);
            return;
        }
        const id = setInterval(
            () => setCursorVisible((v) => !v),
            settings.blinkMs
        );
        return () => clearInterval(id);
    }, [idle, reducedMotion, settings.blinkMs]);

    // Nothing to type if every phrase slot was left empty. All hooks above have
    // already run, so this early return keeps the rules of hooks intact.
    if (phrases.length === 0) {
        return null;
    }

    // Reduced motion: render static phrase 1 with non-blinking cursor
    const displaySegments = (
        reducedMotion ? phrases[0] : currentSegments
    ) as Segment[];
    const displayCount = reducedMotion
        ? phraseLength(phrases[0] as Segment[])
        : charCount;
    const showCursor = reducedMotion ? true : cursorVisible;

    const longestSegments = phrases[longestIndex] as Segment[];
    const longestCount = phraseLength(longestSegments);

    // Resolve all responsive pixel sizing for this breakpoint (see
    // `resolveSizing` for the desktop-vs-small-screen behavior).
    const {
        cursorWidth,
        cursorHeight,
        fontSize,
        padding,
        borderRadius,
        promptGap
    } = resolveSizing(settings, breakpoint);

    // Cursor fills the terminal's content area. The cursor's explicit
    // pixel height drives the terminal's interior height; combined with
    // the box's padding, this leaves padding-px of dark space above and
    // below the cursor. The text is centered alongside it via the
    // inline-flex row.
    // Runtime-only cursor values (static ones live in cursorBaseCss).
    const cursorStyle: React.CSSProperties = {
        width: cursorWidth,
        height: cursorHeight,
        backgroundColor: WHITE,
        opacity: showCursor ? 1 : 0
    };

    // Runtime-only text values (static ones live in textStaticCss).
    const textStyle: React.CSSProperties = {
        fontFamily:
            "'Atlassian Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
        fontSize,
        color: WHITE
    };

    // Explicit box width is desktop-only — on tablet/mobile we always let
    // the ghost measure the natural width so the box scales naturally with
    // the responsive font size.
    const explicitWidth = isDesktop && typeof settings.boxWidthPx === "number";
    // Runtime-only outer values (static ones live in outerCss). The transform
    // in outerCss creates the stacking context the colored puffs land in.
    const outerStyle: React.CSSProperties = {
        borderRadius,
        padding
    };
    if (explicitWidth) {
        outerStyle.minWidth = settings.boxWidthPx as number;
    }

    return (
        <div>
            <div css={[terminalGlowInCss, outerCss]} style={outerStyle}>
                {/* Opaque terminal background layer. Sits above the colored puffs
                    (zIndex: -2) and below the text (which is in normal flow), so
                    it masks the inside-terminal portion of every puff. */}
                <span aria-hidden css={bgLayerCss} style={{ borderRadius }} />
                {topLine ? (
                    <div
                        css={textStaticCss}
                        style={{ ...textStyle, marginBottom: promptGap }}
                    >
                        {topLine}
                    </div>
                ) : null}
                {/* aria-hidden because constantly-changing typed/backspaced text
                    would otherwise spam assistive tech. The static `topLine` heading
                    above stays exposed to screen readers. */}
                <div css={gridContainerCss} aria-hidden>
                    {/* Invisible ghost — reserves width/height of the longest fully-typed phrase + cursor.
                        When an explicit box width is set, the outer container's overflow:hidden clips
                        any horizontal overflow, but the ghost still locks the height. */}
                    <div
                        css={[textStaticCss, rowCss, gridCellCss, ghostCellCss]}
                        style={textStyle}
                    >
                        <span>
                            {renderSegments(
                                longestSegments,
                                longestCount,
                                promptGap,
                                padding,
                                false
                            )}
                        </span>
                        <span
                            css={cursorBaseCss}
                            style={{ ...cursorStyle, opacity: 1 }}
                        />
                    </div>
                    {/* Visible content */}
                    <div
                        css={[textStaticCss, rowCss, gridCellCss]}
                        style={textStyle}
                    >
                        <span>
                            {renderSegments(
                                displaySegments,
                                displayCount,
                                promptGap,
                                padding,
                                true
                            )}
                        </span>
                        {/* Cursor with matching top/bottom white halo sliver. Same
                            behind-the-bg trick as the coloured segment puffs. */}
                        <TerminalCursor
                            cursorStyle={cursorStyle}
                            padding={padding}
                            showCursor={showCursor}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Flat parameters as supplied by Uniform. Each phrase slot is a `richText`
 * value plus a `select` color, keyed `phraseN` / `phraseNColor`. These mirror
 * the parameter ids in `terminalTyping.json`. Slots are independent — an editor
 * can fill any subset; empty slots are skipped.
 */
export interface UniformTerminalTypingProps {
    topLine?: string;
    phrase1?: PhraseInput["richText"];
    phrase1Color?: string;
    phrase2?: PhraseInput["richText"];
    phrase2Color?: string;
    phrase3?: PhraseInput["richText"];
    phrase3Color?: string;
    phrase4?: PhraseInput["richText"];
    phrase4Color?: string;
    phrase5?: PhraseInput["richText"];
    phrase5Color?: string;
    phrase6?: PhraseInput["richText"];
    phrase6Color?: string;
    phrase7?: PhraseInput["richText"];
    phrase7Color?: string;
    phrase8?: PhraseInput["richText"];
    phrase8Color?: string;
}

/**
 * Uniform entry point. Collapses the flat `phraseN` / `phraseNColor` params
 * into the ordered `PhraseInput[]` the animation consumes, then renders the
 * core {@link UniformTerminalTyping} component.
 */
const UniformTerminalTypingComponent = (props: UniformTerminalTypingProps) => {
    const phrases: PhraseInput[] = [];
    for (let i = 1; i <= MAX_PHRASES; i++) {
        const richText =
            props[`phrase${i}` as keyof UniformTerminalTypingProps];
        const highlightColor = props[
            `phrase${i}Color` as keyof UniformTerminalTypingProps
        ] as string | undefined;
        phrases.push({
            richText: richText as PhraseInput["richText"],
            highlightColor
        });
    }
    return <UniformTerminalTyping topLine={props.topLine} phrases={phrases} />;
};

export default UniformTerminalTypingComponent;
